/**
 * useResponsive Hook
 *
 * Переиспользуемый хук для определения типа устройства и адаптивной логики.
 * Основан на MUI breakpoints для консистентности с темой.
 *
 * @example
 * const { isMobile, isTablet, isDesktop, breakpoint } = useResponsive();
 *
 * {isMobile ? <MobileLayout /> : <DesktopLayout />}
 */

import { useMediaQuery, useTheme } from '@mui/material';

/**
 * Hook для определения текущего breakpoint и типа устройства
 * @returns {Object} Объект с responsive состоянием
 */
export const useResponsive = () => {
  const theme = useTheme();

  // Основные breakpoints
  const isXs = useMediaQuery(theme.breakpoints.only('xs')); // 0-599
  const isSm = useMediaQuery(theme.breakpoints.only('sm')); // 600-899
  const isMd = useMediaQuery(theme.breakpoints.only('md')); // 900-1199
  const isLg = useMediaQuery(theme.breakpoints.only('lg')); // 1200-1535
  const isXl = useMediaQuery(theme.breakpoints.only('xl')); // 1536+

  // Категории устройств
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // <600
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-899
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // 900+
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg')); // 1200+

  // Специальные случаи
  const isVerySmallMobile = useMediaQuery('(max-width:400px)'); // <400
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const isPortrait = useMediaQuery('(orientation: portrait)');

  // Touch vs pointer устройства
  const hasHover = useMediaQuery('(hover: hover)'); // Устройство с мышью
  const isTouch = useMediaQuery('(hover: none)'); // Touch устройство
  const hasCoarsePointer = useMediaQuery('(pointer: coarse)'); // Палец (не мышь)
  const hasFinePointer = useMediaQuery('(pointer: fine)'); // Точный указатель (мышь)

  // Определение текущего breakpoint
  const getCurrentBreakpoint = () => {
    if (isXl) return 'xl';
    if (isLg) return 'lg';
    if (isMd) return 'md';
    if (isSm) return 'sm';
    return 'xs';
  };

  // Определение типа устройства
  const getDeviceType = () => {
    if (isMobile) return 'mobile';
    if (isTablet) return 'tablet';
    return 'desktop';
  };

  return {
    // Breakpoints
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,

    // Device categories
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    isVerySmallMobile,

    // Orientation
    isLandscape,
    isPortrait,

    // Input type
    hasHover,
    isTouch,
    hasCoarsePointer,
    hasFinePointer,

    // Computed values
    breakpoint: getCurrentBreakpoint(),
    deviceType: getDeviceType(),
  };
};

/**
 * Hook для получения responsive значений
 * Возвращает разные значения в зависимости от breakpoint
 *
 * @example
 * const padding = useResponsiveValue({ xs: 8, sm: 12, md: 16, lg: 24 });
 * // Вернёт 8 на мобильном, 12 на планшете, и т.д.
 */
export const useResponsiveValue = (values) => {
  const theme = useTheme();

  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  if (isXl && values.xl !== undefined) return values.xl;
  if (isLg && values.lg !== undefined) return values.lg;
  if (isMd && values.md !== undefined) return values.md;
  if (isSm && values.sm !== undefined) return values.sm;
  return values.xs;
};

/**
 * Hook для получения responsive стилей (MOBILE-FIRST)
 * Автоматически применяет стили через media queries
 *
 * @example
 * const styles = useResponsiveStyles({
 *   padding: { xs: '8px', sm: '12px', md: '16px' },
 *   fontSize: { xs: '14px', sm: '16px' },
 * });
 * // Вернёт объект со стилями для текущего breakpoint
 */
export const useResponsiveStyles = (styles) => {
  const theme = useTheme();

  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  const result = {};

  for (const [prop, values] of Object.entries(styles)) {
    if (typeof values === 'object' && values !== null) {
      // Responsive объект: mobile-first логика
      if (isXl && values.xl !== undefined) {
        result[prop] = values.xl;
      } else if (isLg && values.lg !== undefined) {
        result[prop] = values.lg;
      } else if (isMd && values.md !== undefined) {
        result[prop] = values.md;
      } else if (isSm && values.sm !== undefined) {
        result[prop] = values.sm;
      } else {
        result[prop] = values.xs;
      }
    } else {
      // Статическое значение
      result[prop] = values;
    }
  }

  return result;
};

export default useResponsive;
